import React from 'react';
import Chart from 'components/pure/charts/custom/Chart';
import LineSeries from 'components/pure/charts/custom/LineSeries';
import {scaleLinear, scaleTime} from 'd3-scale';
import Axes from 'components/pure/charts/custom/Axes';
import PopoverContainer from 'components/pure/utils/PopoverContainer';
import moment from 'moment';
import CloseIcon from 'components/pure/utils/icon/CloseIcon';
import xor from 'lodash/xor';
import {List} from 'immutable';
import LoadingOverlay from 'components/pure/utils/LoadingOverlay';
import Theme from 'styles/theme';
import './Legend.styl';

const NON_HOVER_COLOR = Theme.palette.ghost;

const LINE_CLIP_PATH = 'lineClipPath';
const MARGIN_FOR_CIRCLES = 10;

/**
 * Fancy trend over time chart.
 *
 * See trend on rollups in manager dash for an example.
 *
 * Supports many usages - pinned/called out trends, a legend, a linear regression,
 * and more.  Many bells and whistles that are fairly tightly coupled to this
 * particular use case.  Heavily discourage adding anything more to this w/o
 * trying to break it apart - fair amount of complexity at the moment.
 *
 * TODO - move into ev-charts
 */
export default class TrendChart extends React.Component {
  constructor(props) {
    super(props);

    this.legendMouseEnter = this.legendMouseEnter.bind(this);
    this.legendMouseLeave = this.legendMouseLeave.bind(this);
    this.escFunction = this.escFunction.bind(this);
    this.togglePin = this.togglePin.bind(this);
    this.onTrendListChange = this.onTrendListChange.bind(this);

    this.state = {
      hoverIndex: null,
      // pinned indices come from interacting with the chart labels
      pinnedIndices: [],
      legendFilteredTrends: List(),
    };
  }

  togglePin(index) {
    const pins = this.state.pinnedIndices;
    const newPins = xor(pins, [index]);
    this.setState({
      ...this.state,
      pinnedIndices: newPins,
    });
  }

  onTrendListChange(legendFilteredTrends) {
    this.setState({legendFilteredTrends, pinnedIndices: []});
  }

  escFunction(event) {
    if (event.keyCode === 27) {
      this.props.onClose();
    }
  }

  componentDidMount() {
    document.addEventListener('keydown', this.escFunction, false);
  }
  componentWillUnmount() {
    document.removeEventListener('keydown', this.escFunction, false);
  }

  legendMouseEnter(hoverIndex) {
    const pins = this.state.pinnedIndices;
    if (pins.length === 0 || pins.includes(hoverIndex)) {
      this.setState({
        hoverIndex,
      });
    }
  }

  legendMouseLeave() {
    this.setState({
      hoverIndex: null,
    });
  }

  render() {
    const {
      trends,
      prefilteredTrends,
      trendMetrics,
      label,
      buttons,
      loading,
      legend,
      preselectedGroup,
      margins,
      startDate,
      endDate,
      yRange = [0, 100],
      yLabel = 'Scores',
    } = this.props;
    const {pinnedIndices, legendFilteredTrends} = this.state;

    return (
      <div className="chart-container">
        <PopoverContainer
          anchorOriginVertical="top"
          targetOriginVertical="bottom"
          containerClassName="chart-indicator"
          hideEmpty={true}
          ref={r => (this.popoverContainer = r)}
        />
        {loading && <LoadingOverlay />}
        {this.renderChart(
          label,
          buttons,
          trends,
          legendFilteredTrends && legendFilteredTrends.size > 0
            ? legendFilteredTrends
            : prefilteredTrends && prefilteredTrends.size > 0
            ? prefilteredTrends
            : trends,
          trendMetrics,
          pinnedIndices,
          legend,
          preselectedGroup,
          margins,
          startDate,
          endDate,
          yRange,
          yLabel
        )}
      </div>
    );
  }

  renderLineSeries(trends, hoverIndex, indicatorOptions, scales, pinnedIndices) {
    const lineSeries = trends
      .filter(item => item.data)
      .map((item, index) => {
        const isHovered = index === hoverIndex;
        const isPinned = pinnedIndices.includes(index);

        // define "default state" as no hover/pin yet.
        const hasPinned = pinnedIndices.filter(i => i !== -1).length > 0;
        const hasHovered = hoverIndex !== null;
        const inDefaultState = !(hasPinned || hasHovered);

        // only show the data points if they wouldn't clutter the screen
        const showDataPoints = trends.size === 1 || isHovered || isPinned;

        return {
          isHovered,
          isPinned,
          series: (
            <LineSeries
              scales={scales}
              data={item.data.toArray()}
              color={isHovered || isPinned || inDefaultState ? item.color : NON_HOVER_COLOR}
              key={'lines' + index}
              indicatorOptions={indicatorOptions}
              tooltipContent={d =>
                this.props.tooltipContent(item, d, index, this.state.pinnedIndices)
              }
              popoverContainer={this.popoverContainer}
              clipPath={LINE_CLIP_PATH}
              makeLinearRegression={trends.size === 1}
              showDataPoints={showDataPoints}
            />
          ),
        };
      });

    // return series, sorted by hovered -> pinned -> the rest (to hack zIndex)
    return lineSeries
      .sort((l, r) => {
        const leftValue = l.isHovered ? 3 : l.isPinned ? 2 : 1;
        const rightValue = r.isHovered ? 3 : r.isPinned ? 2 : 1;

        return leftValue - rightValue;
      })
      .map(i => i.series);
  }

  renderChart(
    label,
    buttons,
    trends,
    filteredTrends,
    trendMetrics,
    indicesToCallout,
    Legend,
    preselectedGroup,
    incomingMargins,
    startDate,
    endDate,
    yRange,
    yLabel
  ) {
    const width = !Legend ? 880 : 640;
    const height = 250;
    const margins = incomingMargins || {
      top: 10,
      left: 40,
      right: 15,
      bottom: 25,
    };
    const svgDimensions = {width: width, height: height};
    const {hoverIndex} = this.state;
    // get the date range
    const allDateEnds = filteredTrends.flatMap(t => [t.firstDate, t.lastDate]).toArray();
    const firstDate = startDate ? startDate : moment.min(allDateEnds);
    const lastDate = endDate ? endDate : moment.max(allDateEnds);
    const displayTrends = filteredTrends.map(trend => {
      const lastValue = trend.data.get(-1);
      const firstValue = trend.data.get(0);

      const firstBridge = firstValue &&
        !firstValue.date.isSameOrBefore(firstDate) && {
          date: firstDate,
          value: firstValue.value,
          indicator: false,
        };

      const lastBridge = lastValue &&
        !lastValue.date.isSameOrAfter(lastDate) && {
          date: lastDate,
          value: lastValue.value,
          indicator: false,
        };

      return {
        ...trend,
        // "fix" the data so that if there is no value for either the first or last date
        // we "bridge" to the the sides of the graph by drawing a line,
        // but not an indicator circle
        data: List()
          .concat(firstBridge)
          .concat(trend.data.map(d => ({...d, indicator: true})))
          .concat(lastBridge)
          .filter(i => i),
      };
    });

    const xScale = scaleTime()
      .domain([firstDate.toDate(), lastDate.toDate()])
      .range([margins.left, svgDimensions.width - margins.right]);

    const yScale = scaleLinear()
      .domain(yRange)
      .range([svgDimensions.height - margins.bottom, margins.top]);

    const scales = {xScale, yScale};

    const indicatorOptions = {
      radius: 2,
    };

    return (
      <div>
        <div className="chart-header">
          <div className="chart-title">{label}</div>
          {buttons}
          <div className="chart-close">
            <CloseIcon onClick={this.props.onClose} />
          </div>
        </div>
        <div className="chart-wrapper">
          <div className="yAxis-label">{yLabel}</div>
          <Chart
            margins={margins}
            svgDimensions={svgDimensions}
            bordered={true}
            className={!Legend && 'no-legend'}
            defs={
              <clipPath id={LINE_CLIP_PATH}>
                <rect
                  x={margins.left}
                  y={margins.top - MARGIN_FOR_CIRCLES}
                  width={width - (margins.left + margins.right)}
                  height={height - (margins.top + margins.bottom) + MARGIN_FOR_CIRCLES * 2}
                />
              </clipPath>
            }>
            <Axes scales={scales} margins={margins} formatValue={this.props.formatValue} />
            {this.renderLineSeries(
              displayTrends,
              hoverIndex,
              indicatorOptions,
              scales,
              indicesToCallout
            )}
          </Chart>
          {Legend && (
            <Legend
              trends={trends}
              trendMetrics={trendMetrics}
              filteredTrends={filteredTrends}
              preselectedGroup={preselectedGroup}
              hoverIndex={hoverIndex}
              pinned={indicesToCallout}
              legendMouseEnter={this.legendMouseEnter}
              legendMouseLeave={this.legendMouseLeave}
              togglePin={this.togglePin}
              onTrendListChange={this.onTrendListChange}
            />
          )}
        </div>
      </div>
    );
  }
}
